/**
 * Controller for the wishlist page (sections/main-wishlist.liquid).
 *
 * The section server-renders the logged-in customer's metafield rows; this
 * element reconciles them with the local working copy: rows removed on this
 * device disappear, and locally stored items (guests, or additions the
 * metafield backend has not stored yet) are resolved through the search-based
 * section render and stacked on top, newest first.
 */
if (!customElements.get('wishlist-page')) {
  customElements.define(
    'wishlist-page',
    class WishlistPage extends HTMLElement {
      connectedCallback() {
        this.itemsElement = this.querySelector('[data-wishlist-items]');
        this.emptyElement = this.querySelector('[data-wishlist-empty]');
        this.footerElement = this.querySelector('[data-wishlist-footer]');
        this.addAllButton = this.querySelector('[data-wishlist-add-all]');
        this.fetchFailed = false;

        this.abortController = new AbortController();
        this.addEventListener('click', this.handleClick, { signal: this.abortController.signal });

        this.reconcile();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      get rows() {
        return [...this.itemsElement.querySelectorAll('[data-wishlist-item]')];
      }

      get disclaimerElement() {
        return this.querySelector('professional-product-disclaimer');
      }

      handleClick = (event) => {
        const removeButton = event.target.closest('[data-wishlist-remove]');
        if (removeButton) {
          const row = removeButton.closest('[data-wishlist-item]');
          WishlistUtils.remove(row.dataset.handle);
          row.remove();
          this.updateVisibility();
          return;
        }

        if (event.target.closest('[data-wishlist-add-all]')) {
          this.handleAddAll();
        }
      };

      async reconcile() {
        const items = WishlistUtils.getItems();
        const handles = new Set(items.map((item) => item.handle));

        // Rows the customer removed on this device before the metafield backend
        // can. Only safe when js-variables actually emitted the metafield state —
        // without it, every server-rendered row would look "removed".
        if (window.FoxTheme.wishlist) {
          this.rows.forEach((row) => {
            if (!handles.has(row.dataset.handle)) row.remove();
          });
        }

        const rendered = new Set(this.rows.map((row) => row.dataset.handle));
        const missing = items.filter((item) => !rendered.has(item.handle)).map((item) => item.handle);

        if (missing.length > 0) {
          // Don't claim "empty" or offer add-all on a partial list while rows load.
          this.emptyElement.hidden = true;
          this.addAllButton?.toggleAttribute('disabled', true);

          const { fragment, failed } = await this.fetchRows(missing);
          this.fetchFailed = failed;
          this.itemsElement.prepend(fragment);
          this.addAllButton?.toggleAttribute('disabled', false);
        }

        this.updateVisibility();
      }

      /**
       * Resolves handles to rendered rows via the search page (handle: is a
       * supported storefront search field). Storefront search truncates the
       * query at ~256 characters — the vendor's recently-viewed section caps
       * itself at 12 ids for the same reason — so the terms are chunked by
       * character budget and fetched in parallel, in order.
       *
       * A handle the search does not return stays stored: the index also omits
       * hidden or just-published products, so a miss is no proof of deletion.
       */
      async fetchRows(handles) {
        const budget = 250;
        const chunks = [];
        let current = [];
        let length = 0;

        handles.forEach((handle) => {
          const term = `handle:${handle}`;
          const projected = current.length === 0 ? term.length : length + 4 + term.length;
          if (current.length > 0 && projected > budget) {
            chunks.push(current);
            current = [];
            length = 0;
          }
          current.push(term);
          length = length === 0 ? term.length : length + 4 + term.length;
        });
        if (current.length > 0) chunks.push(current);

        const results = await Promise.all(
          chunks.map(async (terms) => {
            try {
              const response = await fetch(this.dataset.fetchUrl + encodeURIComponent(terms.join(' OR ')));
              if (!response.ok) return null;
              const html = new DOMParser().parseFromString(await response.text(), 'text/html');
              return [...html.querySelectorAll('[data-wishlist-item]')];
            } catch (error) {
              console.error(error);
              return null;
            }
          })
        );

        const fragment = document.createDocumentFragment();
        results.forEach((rows) => rows?.forEach((row) => fragment.appendChild(row)));
        return { fragment, failed: results.some((rows) => rows === null) };
      }

      updateVisibility() {
        const hasRows = this.rows.length > 0;
        // A failed resolve must not claim the wishlist is empty — the items are still stored.
        this.emptyElement.hidden = hasRows || this.fetchFailed;
        this.footerElement.hidden = !hasRows;
      }

      handleAddAll() {
        // Sold-out is flagged on the row: FoxTheme's ProductForm re-enables the
        // hidden variant-id input on upgrade, so its disabled state is unusable.
        const addableRows = this.rows.filter(
          (row) => !row.hasAttribute('data-sold-out') && row.querySelector('[name="id"]')
        );
        if (addableRows.length === 0) return;

        const items = addableRows.map((row) => ({
          id: Number(row.querySelector('[name="id"]').value),
          quantity: Math.max(1, Number(row.querySelector('.quantity__input')?.value) || 1),
        }));

        const proceed = () => this.addItemsToCart(items);
        const disclaimer = this.disclaimerElement;
        const hasProfessional = addableRows.some((row) =>
          row.querySelector('form[data-professional-product="true"]')
        );

        if (hasProfessional && disclaimer) {
          // Fail closed while the disclaimer script is still loading: skipping
          // the confirmation is worse than a click that does nothing yet.
          if (typeof disclaimer.requestConfirmation === 'function') {
            disclaimer.requestConfirmation(proceed, this.addAllButton);
          }
          return;
        }

        proceed();
      }

      // Mirrors products-bundle.js, the theme's existing multi-line add-to-cart.
      addItemsToCart(items) {
        if (document.body.classList.contains('cart-template') || FoxTheme.settings.cartType !== 'drawer') {
          FoxTheme.utils.postLink(FoxTheme.routes.cart_add_url, { parameters: { items } });
          return;
        }

        this.showErrorMessage();
        this.toggleAddAllLoading(true);

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({ items, sections: sectionsToBundle, section_url: window.location.pathname });

        fetch(`${FoxTheme.routes.cart_add_url}`, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.showErrorMessage(parsedState.description);
              return;
            }

            const cartJson = await (
              await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })
            ).json();
            cartJson['sections'] = parsedState['sections'];

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));
            document.querySelector('cart-drawer')?.show(this.addAllButton);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.toggleAddAllLoading(false);
          });
      }

      showErrorMessage(errorMessage = false) {
        this.errorMessageElement = this.errorMessageElement || this.querySelector('[data-wishlist-error]');
        if (this.errorMessageElement) {
          this.errorMessageElement.hidden = !errorMessage;
          if (errorMessage) this.errorMessageElement.textContent = errorMessage;
        } else if (errorMessage) {
          alert(errorMessage);
        }
      }

      toggleAddAllLoading(status) {
        if (!this.addAllButton) return;
        this.addAllButton.classList.toggle('btn--loading', status);
        this.addAllButton.toggleAttribute('disabled', status);
      }
    }
  );
}
